import React, { useEffect, useMemo, useState } from 'react';
import Classes from './RecordTable.module.css';
import useScreenWidth from '../../../../../../../../../Hooks/useScreenWidth';
import SkeletonLoading from '../../../../../../../../GlobalComponents/SkeletonLoading/SkeletonLoading';
import { Pagination, Stack } from '@mui/material';
import TableRow from './TableRow';
import { useNavigate } from 'react-router-dom';
import { AddIcon } from '../../../../../../../../../utils/Icons';

const STATUS_UI = {
  paid: 'Paid',
  completed: 'Paid',   // 👈 often used by backend for paid invoices
  pending: 'Pending',
  overdue: 'Overdue',
  void: 'Void',
};

function asCurrency(total, currency = 'usd') {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency?.toUpperCase?.() || 'USD',
      currencyDisplay: 'symbol',
    }).format(Number(total || 0));
  } catch {
    return `$${Number(total || 0).toFixed(2)}`;
  }
}

function asDate(d) {
  if (!d) return '-';
  try {
    return new Date(d).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return String(d);
  }
}

export default function RecordTable({ loading, invoices = [], userId }) {
  const navigateTo = useNavigate();
  const screenWidth = useScreenWidth();
  const isMobile = screenWidth <= 480;

  // derive table rows from real invoices
  const rows = useMemo(() => {
    return (invoices || []).map(inv => {
      const currency = inv?.currency || 'usd';
      const total = Math.max(0, Number(inv?.total || 0));
      const refunded = Math.max(0, Number(inv?.refundedTotal || 0));
      const netTotal = Math.max(0, total - refunded);

      return {
        _id: inv._id,
        Number: inv.invoiceNumber || inv.number || (inv._id || '').slice(-7).toUpperCase(),
        Status: STATUS_UI[String(inv.status || '').toLowerCase()] || inv.status || 'Pending',
        Total: asCurrency(netTotal, currency),                         // 👈 show total AFTER refunds
        RefundedLabel: refunded > 0 ? `Refunded ${asCurrency(refunded, currency)}` : '', // 👈 badge text
        RefundedRaw: refunded,
        Created: asDate(inv.issuedAt || inv.createdAt),
        Paid: inv.paidAt ? asDate(inv.paidAt) : '-',
      };
    });
  }, [invoices]);

  const [data, setData] = useState(rows);
  useEffect(() => setData(rows), [rows]);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;
  const totalPages = Math.max(1, Math.ceil((data?.length || 0) / itemsPerPage));
  const startIdx = (currentPage - 1) * itemsPerPage;
  const currentItems = data.slice(startIdx, startIdx + itemsPerPage);
  const startItem = data.length ? startIdx + 1 : 0;
  const endItem = Math.min(startIdx + itemsPerPage, data.length);
  const totalItems = data.length;

  function handlePageChange(_, pageNum) { setCurrentPage(pageNum); }

  // allow removing a row after delete/void
  const handleRemoved = (invoiceId) => {
    setData(prev => prev.filter(r => r._id !== invoiceId));
  };

  return (
    <div className={Classes.hero}>
      <div className={Classes.table}>
        <div className={Classes.tableWrapper}>

          <div className={Classes.tableHeader}>
            <div className={`${Classes.headerItem} ${Classes.header1}`}><h5>Number</h5></div>
            {/* Client column is hidden in client context */}
            <div className={`${Classes.headerItem} ${Classes.header3}`}><h5>Status</h5></div>
            <div className={`${Classes.headerItem} ${Classes.header4}`}><h5>Total</h5></div>
            <div className={`${Classes.headerItem} ${Classes.header5}`}><h5>Created</h5></div>
            <div className={`${Classes.headerItem} ${Classes.header6}`}><h5>Paid</h5></div>
            <div className={`${Classes.headerItem} ${Classes.header7}`}>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button className={Classes.addBtn} onClick={() => navigateTo(`/add-invoice/${userId}`)}>
                  <AddIcon height='12px' fill='white' />
                  <h5>Add invoice</h5>
                </button>
              </div>
            </div>
          </div>

          <div className={Classes.tableBody}>
            {loading ? (
              <SkeletonLoading
                length={itemsPerPage}
                height={isMobile ? '3.5rem' : '2.8rem'}
                width="100%"
                br="6px"
                padding="0"
              />
            ) : currentItems && currentItems.length > 0 ? (
              currentItems.map((row, idx) => (
                <TableRow key={row._id} value={row} index={idx} onRemoved={handleRemoved} />
              ))
            ) : (
              <h5 className='placeholderText' style={{ margin: '10px 19px' }} >No invoice yet...</h5>
            )}
          </div>

          {currentItems && currentItems.length > 0 &&
            <div className={Classes.pagFooter}>
              <h5 style={{ height: '1.2rem' }}>
                {loading
                  ? <SkeletonLoading height='1.2rem' width='10rem' br='4px' padding='0' />
                  : `Showing ${startItem} to ${endItem} of ${totalItems} results`}
              </h5>
              <Stack spacing={2} className={Classes.pagination}>
                {loading
                  ? <SkeletonLoading height='1.2rem' width='5rem' br='4px' padding='0' />
                  : <Pagination count={totalPages} page={currentPage} onChange={handlePageChange} shape="rounded" />}
              </Stack>
            </div>
          }

        </div>
      </div>
    </div>
  );
}
